import { getFirestore, collection, doc, getDocs, getDoc, addDoc, updateDoc, serverTimestamp, orderBy, query } from "firebase/firestore";
import { app } from "../../firebase-config.js";
import { DaVinciEvents } from "../../lab/davinci/core/DaVinciEvents.js";
import { DaVinciState } from "../../lab/davinci/core/DaVinciState.js";
import { DaVinciCanvas } from "../../lab/davinci/components/DaVinciCanvas.js";
import "../../lab/davinci/components/DaVinciToolbar.js"; // Auto-init Singleton
import { DaVinciToolbox } from "../../lab/davinci/components/DaVinciToolbox.js";

// [CSS Injection] Ensure Da Vinci styles are loaded
import "../../lab/davinci/styles/davinci.css";

const db = getFirestore(app);

/**
 * DaVinciEditor
 * 
 * The main controller for the Cine-Novel Editor within Author Studio.
 * Manages the "Hub View" (Episode List) and the "Editor View" (Canvas).
 * 
 * Data Architecture:
 * - Series: Metadata (stored in 'series' collection)
 * - Episodes: Content (stored in 'series/{id}/episodes' sub-collection)
 * - Blocks: The JSON structure within an episode doc.
 */
export class DaVinciEditor {
    constructor(container, seriesId, data) {
        this.container = container;
        this.seriesId = seriesId;
        this.seriesData = data;
        this.episodes = [];
        this.currentEpisodeId = null;
        this.viewMode = 'hub'; // 'hub' | 'editor'

        // Cleanups
        this.eventCleanups = [];

        this.init();
    }

    async init() {
        await this.loadEpisodes();
        this.render();
    }

    async loadEpisodes() {
        try {
            const q = query(
                collection(db, "series", this.seriesId, "episodes"),
                orderBy("order", "asc") // [FIX] Sort by Order Index (not Time)
            );
            const snapshot = await getDocs(q);
            this.episodes = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (e) {
            console.error("Failed to load episodes:", e);
        }
    }

    render() {
        // Clear Container
        this.container.innerHTML = '';
        this.cleanupEvents(); // Clear previous editor bindings if any

        if (this.viewMode === 'hub') {
            this.renderHub();
        } else {
            this.renderEditor();
        }
    }

    // --- Hub View (Episode List) ---
    renderHub() {
        // [FIX] Background Styles
        this.container.style.backgroundColor = '#121212';
        this.container.style.fontFamily = "'Noto Sans KR', sans-serif";

        const bg = document.createElement('div');
        bg.className = "absolute inset-0 bg-cover bg-center z-0";
        bg.style.backgroundImage = "url('/login.webp')";
        bg.style.filter = "brightness(0.3) blur(2px)";
        bg.style.zIndex = "0";
        this.container.appendChild(bg); // Append background first

        const wrapper = document.createElement('div');
        wrapper.className = "relative z-10 max-w-5xl mx-auto p-8"; // Added relative z-10

        // 1. Header
        const header = document.createElement('div');
        header.className = "flex justify-between items-center mb-10";
        header.innerHTML = `
            <div>
                <button id="btn-back-dashboard" class="flex items-center text-zinc-400 hover:text-white mb-2 text-sm font-bold transition-colors">
                    <span class="material-symbols-outlined text-lg mr-1">arrow_back</span> ${window.locale.t('editor.back_to_list')}
                </button>
                <div class="flex items-center gap-3">
                    <span class="bg-rose-500/20 text-rose-400 border border-rose-500/30 p-2 rounded-lg">
                         <span class="material-symbols-outlined text-2xl">movie</span>
                    </span>
                    <div>
                        <h2 class="text-3xl font-black text-white tracking-tight">${this.seriesData.title}</h2>
                        <p class="text-zinc-500 text-sm">${window.locale.t('common.cinenovel')} ${window.locale.t('common.editor') || 'Editor'}</p>
                    </div>
                </div>
            </div>
            <button id="btn-new-episode" class="bg-rose-600 hover:bg-rose-500 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-rose-900/20 active:scale-95 transition-all">
                <span class="material-symbols-outlined">add</span> ${window.locale.t('editor.new_episode')}
            </button>
        `;
        wrapper.appendChild(header);

        // 2. Statistics / Meta (Optional)
        // ...

        // 3. Episode List
        const list = document.createElement('div');
        list.className = "space-y-4";

        if (this.episodes.length === 0) {
            list.innerHTML = `
                <div class="p-20 text-center border-2 border-dashed border-zinc-800 rounded-3xl bg-zinc-900/30 group cursor-pointer" id="empty-state-trigger">
                    <span class="material-symbols-outlined text-6xl text-zinc-700 mb-4 group-hover:text-rose-500 transition-colors">movie_edit</span>
                    <p class="text-xl font-bold text-zinc-400 group-hover:text-white transition-colors">${window.locale.t('studio.create_first_episode')}</p>
                    <p class="text-sm text-zinc-600 mt-2">${window.locale.t('studio.create_first_episode_desc')}</p>
                </div>
            `;
            // Bind empty state click
            setTimeout(() => {
                const trigger = list.querySelector('#empty-state-trigger');
                if (trigger) trigger.onclick = () => this.createEpisode();
            }, 0);
        } else {
            this.episodes.forEach((ep, idx) => {
                const item = document.createElement('div');
                item.className = "group bg-zinc-900 border border-zinc-800 p-5 rounded-2xl flex justify-between items-center hover:border-rose-500/50 hover:bg-zinc-800/50 transition-all cursor-pointer shadow-sm";

                // Formatted Date
                const dateStr = ep.updatedAt?.toDate ? ep.updatedAt.toDate().toLocaleDateString() : 'Draft';

                item.innerHTML = `
                    <div class="flex items-center gap-6">
                        <span class="text-zinc-600 font-mono text-lg font-bold w-8 text-center group-hover:text-rose-500 transition-colors">${idx + 1}</span>
                        
                        <div class="w-24 h-16 bg-zinc-950 rounded-lg overflow-hidden border border-zinc-700 relative">
                             ${ep.thumbnail
                        ? `<img src="${ep.thumbnail}" class="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity">`
                        : `<div class="w-full h-full flex items-center justify-center text-zinc-700"><span class="material-symbols-outlined">image</span></div>`
                    }
                        </div>

                        <div>
                            <h3 class="font-bold text-lg text-white group-hover:text-rose-300 transition-colors mb-1">${ep.title || window.locale.t('editor.untitled_episode')}</h3>
                            <div class="flex items-center gap-3 text-xs text-zinc-500">
                                <span>${window.locale.t('studio.last_updated', { date: dateStr })}</span>
                                <span class="w-1 h-1 bg-zinc-700 rounded-full"></span>
                                <span class="${ep.status === 'published' ? 'text-green-500' : 'text-zinc-500'} font-bold uppercase">${ep.status === 'published' ? (window.locale.t('common.published') || 'Published') : (window.locale.t('common.draft') || 'Draft')}</span>
                            </div>
                        </div>
                    </div>

                    <div class="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity translate-x-4 group-hover:translate-x-0 duration-300">
                        <button class="btn-edit p-3 rounded-xl bg-zinc-800 hover:bg-white hover:text-black text-white transition-colors shadow-lg" title="${window.locale.t('editor.edit')}">
                            <span class="material-symbols-outlined">edit</span>
                        </button>
                        ${(window.user?.role === 'admin' || window.user?.isSuperAdmin) ? `
                        <button class="btn-ai-analyze p-3 rounded-xl bg-zinc-800 hover:bg-purple-500 hover:text-white text-zinc-400 transition-colors shadow-lg" title="${window.locale.t('editor.ai_analyze_admin')}" onclick="event.stopPropagation(); this.dispatchEvent(new CustomEvent('ai-analyze', { bubbles: true, detail: { id: '${ep.id}' } }))">
                            <span class="material-symbols-outlined">smart_toy</span>
                        </button>` : ''}
                        <button class="btn-delete p-3 rounded-xl bg-zinc-800 hover:bg-red-500 hover:text-white text-zinc-400 transition-colors shadow-lg" title="${window.locale.t('editor.delete')}" onclick="event.stopPropagation(); this.dispatchEvent(new CustomEvent('episode-delete', { bubbles: true, detail: { id: '${ep.id}', title: '${ep.title || 'Untitled'}' } }))">
                            <span class="material-symbols-outlined">delete</span>
                        </button>
                    </div>
                `;

                // Click handlers
                item.onclick = (e) => this.openEpisode(ep.id);
                list.appendChild(item);
            });
        }
        list.addEventListener('ai-analyze', async (e) => {
            const epId = e.detail.id;
            if (!await window.Modal.confirm(window.locale.t('editor.ai_analyze_confirm'), window.locale.t('common.confirm') || "AI Confirm")) return;

            try {
                window.Modal.toast(window.locale.t('editor.ai_analyzing'));
                const { getFunctions, httpsCallable } = await import("firebase/functions");
                const functions = getFunctions(app);
                const triggerOCR = httpsCallable(functions, 'manuallyTriggerOCR');

                const result = await triggerOCR({
                    mode: 'davinci',
                    seriesId: this.seriesId,
                    episodeId: epId,
                    uid: window.user.uid
                });

                const resData = result.data;
                window.Modal.alert(`Analysis Complete!\nProcessed: ${resData.successCount}/${resData.processed}`);
            } catch (err) {
                console.error(err);
                window.Modal.alert("Error: " + err.message);
            }
        });

        // [NEW] Bind Delete Event
        list.addEventListener('episode-delete', async (e) => {
            const { id, title } = e.detail;
            if (!await window.Modal.confirm(window.locale.t('studio.delete_modal_content', { title }), window.locale.t('studio.delete_modal_title'))) return;

            try {
                const { DaVinciService } = await import("../../lab/davinci/DaVinciService.js");
                const res = await DaVinciService.deleteEpisode(this.seriesId, id);

                if (res.success) {
                    window.Modal.toast(window.locale.t('common.success') || "Episode deleted.");
                    await this.loadEpisodes();
                    this.render();
                } else {
                    throw new Error(res.error);
                }
            } catch (err) {
                console.error(err);
                window.Modal.alert("Delete Failed: " + err.message);
            }
        });

        wrapper.appendChild(list);

        this.container.appendChild(wrapper);

        // Bind Hub Events
        const btnBack = wrapper.querySelector('#btn-back-dashboard');
        if (btnBack) btnBack.onclick = () => window.router.navigate('/studio');

        const btnNew = wrapper.querySelector('#btn-new-episode');
        if (btnNew) btnNew.onclick = () => this.createEpisode();
    }

    // --- Core Logic ---
    async createEpisode() {
        try {
            window.Modal.toast("새 에피소드 생성 중...");

            // [FIX] Anti-Collision: Refresh Data from DB first
            // Ensures we have the latest 'order' even if multiple tabs/users are active.
            await this.loadEpisodes();

            // [FIX v1.36.204] Roboust Order Calculation
            // Prevent collisions: Use Max(order) + 1 instead of Length + 1
            const maxOrder = this.episodes.reduce((max, ep) => Math.max(max, Number(ep.order) || 0), 0);
            const nextOrder = maxOrder + 1;

            const newEp = {
                title: `Episode ${nextOrder}`,
                blocks: [],
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                status: 'draft',
                type: 'davinci',
                order: nextOrder
            };
            const colRef = collection(db, "series", this.seriesId, "episodes");
            const docRef = await addDoc(colRef, newEp);

            await this.loadEpisodes();
            // Directly open editor
            this.openEpisode(docRef.id);
        } catch (e) {
            console.error(e);
            window.Modal.alert("Error: " + e.message);
        }
    }

    async openEpisode(epId) {
        this.currentEpisodeId = epId;

        // Fetch fresh data
        const docRef = doc(db, "series", this.seriesId, "episodes", epId);
        const snapshot = await getDoc(docRef);

        if (!snapshot.exists()) {
            window.Modal.alert("에피소드를 찾을 수 없습니다.");
            this.viewMode = 'hub';
            this.render();
            return;
        }

        const data = snapshot.data();

        // ** Initialize DaVinci State **
        DaVinciState.init({
            id: epId,
            isProjectInitialized: true,
            title: data.title || '',
            // [FIX] Load Order to prevent "Max Order + 1" auto-increment bug
            order: data.order || 0,
            // [FIX] Load metadata (thumbnail, bgm) to prevent reset
            metadata: {
                thumbnail: data.thumbnail || data.formattedThumbnail || null,
                bgm: data.bgmUrl || (data.metadata ? data.metadata.bgm : null)
            },
            // [FIX] Legacy Compatibility: If status is missing, assume 'published' (Live)
            // This prevents accidental unpublishing when saving old episodes.
            status: data.status || 'published'
        });
        DaVinciState.setBlocks(data.blocks || []);

        this.viewMode = 'editor';
        this.render();
    }

    // --- Editor View (The Workspace) ---
    renderEditor() {
        const wrapper = document.createElement('div');
        wrapper.className = "flex flex-col h-screen bg-black overflow-hidden relative";

        // 1. Editor Header (Custom Toolbar)
        const header = document.createElement('div');
        header.className = "h-14 border-b border-zinc-800 bg-zinc-900 flex justify-between items-center px-4 shrink-0 z-50";
        header.innerHTML = `
            <div class="flex items-center gap-4">
                <button id="btn-editor-back" class="text-zinc-400 hover:text-white transition-colors">
                    <span class="material-symbols-outlined">arrow_back</span>
                </button>
                <div class="h-6 w-px bg-zinc-700"></div>
                <input type="text" id="editor-title-input" 
                    class="bg-transparent border-none focus:ring-0 text-white font-bold text-lg placeholder-zinc-600 w-96" 
                    value="${DaVinciState.get().title}" placeholder="${window.locale.t('editor.input_title_placeholder')}">
            </div>

            <div class="flex items-center gap-2">
                 <span class="text-xs text-zinc-500 mr-2 opacity-0 transition-opacity" id="auto-save-indicator">${window.locale.t('editor.saved')}</span>
                 
                 <button id="btn-editor-preview" class="px-3 py-1.5 text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-lg text-sm font-medium transition-colors border border-transparent hover:border-zinc-700">
                    ${window.locale.t('editor.preview')}
                 </button>
                 
                 ${(window.user?.role === 'admin' || window.user?.isSuperAdmin) ? `
                 <button id="btn-ai-analyze-local" class="px-3 py-1.5 text-indigo-400 hover:text-white hover:bg-indigo-500/20 rounded-lg text-sm font-medium transition-colors border border-transparent hover:border-indigo-500/30 flex items-center gap-1" title="${window.locale.t('editor.ai_analyze_local')}">
                    <span class="material-symbols-outlined text-sm">smart_toy</span>
                 </button>` : ''}

                 <div class="h-6 w-px bg-zinc-700 mx-2"></div>

                 <!-- [FIX] Restore Generic Save Button (Preserve Status) -->
                 <button id="btn-editor-save" class="px-4 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-sm font-bold transition-all border border-zinc-700" title="${window.locale.t('studio.save_preserve_status')}">
                    ${window.locale.t('editor.save')}
                 </button>

                 <button id="btn-save-draft" class="px-4 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-sm font-bold transition-all border border-zinc-700">
                    ${window.locale.t('editor.save_draft')}
                 </button>

                 <button id="btn-publish" class="px-4 py-1.5 bg-rose-600 hover:bg-rose-500 text-white rounded-lg text-sm font-bold shadow-lg shadow-rose-900/20 active:scale-95 transition-all">
                    ${window.locale.t('editor.publish')}
                 </button>
            </div>
        `;
        wrapper.appendChild(header);

        // 2. Workspace (Toolbox + Canvas)
        const workspace = document.createElement('div');
        workspace.className = "flex-1 flex overflow-hidden relative";

        // Render DaVinci Components
        const toolbox = DaVinciToolbox.render();
        const canvas = DaVinciCanvas.render();

        workspace.appendChild(toolbox);
        workspace.appendChild(canvas);
        wrapper.appendChild(workspace);

        this.container.appendChild(wrapper);

        // --- Bind Editor Events ---

        // [FIX] Custom Toast Helper
        const showToast = (message) => {
            const toast = document.createElement('div');
            toast.className = 'fixed bottom-10 left-1/2 transform -translate-x-1/2 bg-zinc-800 text-white px-6 py-3 rounded-full shadow-2xl z-[10000] flex items-center gap-2 border border-zinc-700 animate-fade-in';
            toast.style.cssText = "font-family: 'Noto Sans KR', sans-serif;";
            toast.innerHTML = `<span class="material-symbols-outlined text-green-400">check_circle</span> <span>${message}</span>`;
            document.body.appendChild(toast);
            setTimeout(() => {
                toast.style.opacity = '0';
                toast.style.transition = 'opacity 0.5s';
                setTimeout(() => toast.remove(), 500);
            }, 2000);
        };

        // [FIX] Auto Save Debounce
        let autoSaveTimer = null;
        const triggerAutoSave = () => {
            if (autoSaveTimer) clearTimeout(autoSaveTimer);
            autoSaveTimer = setTimeout(() => {
                this.saveEpisode(false, true); // Silent auto-save
            }, 3000);
        };

        // Back
        header.querySelector('#btn-editor-back').onclick = async () => {
            await this.saveEpisode(); // Save on exit
            this.viewMode = 'hub';
            this.loadEpisodes().then(() => this.render());
        };

        // Title Change
        const titleInput = header.querySelector('#editor-title-input');
        titleInput.oninput = (e) => {
            const val = e.target.value;
            DaVinciState.get().title = val;
            DaVinciCanvas.updateTitle(val);
            triggerAutoSave(); // Auto-save on title
        };

        // [FIX] Button Handlers
        header.querySelector('#btn-editor-save').onclick = () => this.saveEpisode(true, false); // Generic Save (Preserve Status)
        header.querySelector('#btn-save-draft').onclick = () => this.saveEpisode(true, false, 'draft');
        header.querySelector('#btn-publish').onclick = async () => {
            if (await window.Modal.confirm(window.locale.t('editor.publish_confirm'), window.locale.t('editor.publish'))) {
                this.saveEpisode(true, false, 'published');
            }
        };

        // Preview
        header.querySelector('#btn-editor-preview').onclick = async () => {
            await this.saveEpisode(true, false); // Save current state
            // Internal Popup Logic
            const popup = document.createElement('div');
            popup.className = "fixed inset-0 z-[9999] bg-black overflow-y-auto";
            popup.id = "davinci-preview-popup";
            document.body.appendChild(popup);

            import("../../components/feature/viewer/DaVinciViewer.js").then(async ({ DaVinciViewer }) => {
                // [NEW] Dynamic Import Translation Manager
                const { DaVinciTranslationManager } = await import("../../components/feature/viewer/DaVinciTranslationManager.js");

                const manager = new DaVinciTranslationManager(popup, {
                    seriesId: this.seriesId,
                    episodeId: this.currentEpisodeId // Use REAL IDs for OCR Fetch
                });

                const viewerInstance = await DaVinciViewer(popup, {
                    blocks: JSON.parse(JSON.stringify(DaVinciState.get().blocks)),
                    metadata: { ...DaVinciState.get().metadata, title: DaVinciState.get().title },
                    user: window.user,
                    rootElement: popup,
                    navigation: {
                        hasPrev: false,
                        hasNext: false,
                        seriesId: this.seriesId, // Pass Real ID
                        episodeId: this.currentEpisodeId, // Pass Real ID
                        backIcon: 'close',
                        onBack: () => {
                            popup.remove();
                            if (viewerInstance.destroy) viewerInstance.destroy();
                        }
                    },
                    // [NEW] Inject Translation Handler
                    onTranslate: (isEnabled) => manager.toggle(isEnabled)
                });

                popup.addEventListener('close-viewer', () => {
                    if (viewerInstance.destroy) viewerInstance.destroy();
                    popup.remove();
                });
            });
        };

        // AI Analysis (Backfill)
        const analyzeBtn = header.querySelector('#btn-ai-analyze-local');
        if (analyzeBtn) {
            analyzeBtn.onclick = async () => {
                if (!await window.Modal.confirm(window.locale.t('editor.ai_context_analysis_confirm'), window.locale.t('editor.ai_confirm'))) return;
                await this.saveEpisode(true, true); // Silent Autosave to ensure consistency

                try {
                    window.Modal.toast(window.locale.t('editor.ai_analyzing_background'));
                    const { getFunctions, httpsCallable } = await import("firebase/functions");
                    const functions = getFunctions(app);
                    const triggerOCR = httpsCallable(functions, 'manuallyTriggerOCR');

                    const result = await triggerOCR({
                        mode: 'davinci',
                        seriesId: this.seriesId,
                        episodeId: this.currentEpisodeId,
                        uid: window.user.uid
                    });

                    const resData = result.data;

                    if (resData.processed === 0 && resData.debugLogs && resData.debugLogs.length > 0) {
                        // [DEBUG] Show detailed logs if 0 items processed to help user diagnose
                        const logMsg = resData.debugLogs.join('\n');
                        window.Modal.alert(`${window.locale.t('editor.ai_no_items_found')} (0).\n\n[Log]\n${logMsg}`, window.locale.t('editor.diagnosis_report') || 'Report');
                    } else {
                        window.Modal.alert(`분석 완료!\n처리된 데이터: ${resData.successCount}/${resData.processed}`);
                    }
                } catch (err) {
                    console.error(err);
                    window.Modal.alert("Error: " + err.message);
                }
            };
        }

        // --- Attach DaVinci Event Listeners ---
        const refreshCanvas = () => DaVinciCanvas.refreshBlocks();
        // [FIX] Event Listener Split: 
        // 1. Structural Changes -> Refresh DOM + Auto Save
        const onStructureChange = () => {
            refreshCanvas();
            triggerAutoSave();
        };

        // 2. Content Changes -> Auto Save ONLY (No Re-render)
        // This fixes the "1 char input bug" by keeping the DOM intact while typing.
        // 2. Content Changes -> Auto Save ONLY (No Re-render)
        // This fixes the "1 char input bug" by keeping the DOM intact while typing.
        const onContentChange = (payload) => {
            // [FIX] Reactivity for Optimistic UI
            // If the update involves URL or Uploading status, we MUST re-render.
            // Payload might be { id } or { id, ...updates } depending on implementation. 
            // Since we don't receive the full diff here easily without checking state,
            // we can check the Block Type or assume if it's an Image Block we might need to refresh?
            // Better: Check if the block in State has 'isUploading' or changed URL.

            const block = DaVinciState.get().blocks.find(b => b.id === payload.id);
            if (block && (block.type === 'image' || block.isUploading !== undefined)) {
                refreshCanvas(); // Force render for images to show spinner/preview
            }

            triggerAutoSave();
        };

        this.eventCleanups.push(DaVinciEvents.on(DaVinciEvents.ON_BLOCK_ADD, onStructureChange));
        this.eventCleanups.push(DaVinciEvents.on(DaVinciEvents.ON_BLOCK_DELETE, onStructureChange));
        this.eventCleanups.push(DaVinciEvents.on(DaVinciEvents.ON_BLOCK_MOVE, onStructureChange));

        // Content update only triggers save, not re-render
        this.eventCleanups.push(DaVinciEvents.on(DaVinciEvents.ON_BLOCK_UPDATE, onContentChange));

        // Image Upload
        // Image Upload (Optimistic UI)
        this.eventCleanups.push(DaVinciEvents.on('block:request-upload', async ({ id, file }) => {
            const { DaVinciService } = await import("../../lab/davinci/DaVinciService.js");

            // 1. Optimistic Update: Show Blob immediately & Spinner
            const blobUrl = URL.createObjectURL(file);
            DaVinciState.updateBlock(id, { url: blobUrl, isUploading: true });

            // 2. Background Upload
            const path = `users/${window.user.uid}/davinci/${this.seriesId}/ep_${this.currentEpisodeId}`;
            const result = await DaVinciService.uploadImage(file, path);

            if (result.success) {
                // 3. Success: Replace Blob with Real URL, Remove Spinner
                // Wait a tiny bit for perceived smoothness
                setTimeout(() => {
                    DaVinciState.updateBlock(id, { url: result.url, isUploading: false });
                    window.Modal.toast(window.locale.t('editor.upload_complete'));
                    triggerAutoSave();

                    // Trigger Image Analysis (OCR) separately if needed, but let's stick to cost-saving.
                    // Only trigger if user wants? Or keep the auto-ocr for images as it was efficient?
                    // [FIX] User reported duplicate analysis. Removing auto-trigger here. 
                    // Analysis will run on Publish or Manual Trigger.
                    // this._triggerImageAnalysis(this.seriesId, this.currentEpisodeId, DaVinciState.get().blocks);
                }, 500);
            } else {
                // 4. Failure: Remove loading, maybe Revert?
                const currentBlock = DaVinciState.get().blocks.find(b => b.id === id);
                // Keep the blob? Or show error?
                DaVinciState.updateBlock(id, { isUploading: false }); // Stop spinner
                window.Modal.alert(window.locale.t('editor.upload_fail') + result.error);
            }
        }));
    }

    setUnsavedChange(isUnsaved) {
        // Deprecated visual indicator in favor of Auto Save Indicator
    }

    cleanupEvents() {
        this.eventCleanups.forEach(unsub => unsub());
        this.eventCleanups = [];
    }

    // [FIX] Enhanced Save Episode
    async saveEpisode(notify = false, silent = false, targetStatus = null) {
        if (!this.currentEpisodeId) return;

        const state = DaVinciState.get();
        const rawBlocks = state.blocks || [];
        const cleanBlocks = JSON.parse(JSON.stringify(rawBlocks));
        const cleanMetadata = JSON.parse(JSON.stringify(state.metadata || {}));

        // [FIX] Logic moved to Service Layer (DaVinciService.saveEpisode)
        const intendedStatus = targetStatus || state.status || 'draft';

        // [FIX v1.36.204] Safe Order Fallback
        let safeOrder = state.order;
        if (!safeOrder) {
            const maxOrder = this.episodes.reduce((max, ep) => Math.max(max, Number(ep.order) || 0), 0);
            safeOrder = maxOrder + 1;
        }

        const dataToSave = {
            title: state.title || 'Untitled',
            blocks: cleanBlocks,
            metadata: cleanMetadata,
            updatedAt: serverTimestamp(),
            order: safeOrder,
            status: intendedStatus // Send pure intent
        };

        if (!dataToSave.metadata.thumbnail) {
            const firstImage = (cleanBlocks || []).find(b => b.type === 'image' && b.url);
            if (firstImage) {
                dataToSave.metadata.thumbnail = firstImage.url;
                dataToSave.thumbnail = firstImage.url;
            }
        } else {
            dataToSave.thumbnail = dataToSave.metadata.thumbnail;
        }

        try {
            if (notify) {
                // Use Custom Toast
                const showToast = (msg) => {
                    const t = document.createElement('div');
                    t.className = 'fixed bottom-10 left-1/2 transform -translate-x-1/2 bg-zinc-800 text-white px-6 py-3 rounded-full shadow-2xl z-[10000] border border-zinc-700 animate-fade-in';
                    t.innerHTML = `<span class="material-symbols-outlined text-green-400">check_circle</span> <span>${msg}</span>`;
                    document.body.appendChild(t);
                    setTimeout(() => t.remove(), 2500);
                };
                showToast(targetStatus === 'published' ? (window.locale.t('editor.saving') || "Publishing...") : (window.locale.t('editor.saving') || "Saving..."));
            }

            const docRef = doc(db, "series", this.seriesId, "episodes", this.currentEpisodeId);
            const { setDoc } = await import("firebase/firestore");

            // [CHANGED] Use Service instead of direct setDoc? 
            // The Editor was using direct setDoc. 
            // If we want to use the Logic, we MUST use DaVinciService.saveEpisode.
            // But DaVinciService.saveEpisode is async and handles the write.
            // Let's replace this direct write with Service Call.

            const { DaVinciService } = await import("../../lab/davinci/DaVinciService.js");
            // Mapping args: seriesId, episodeData
            const startData = { ...dataToSave, id: this.currentEpisodeId };
            const res = await DaVinciService.saveEpisode(this.seriesId, startData);

            if (res.success) {
                // Update Local State with Trusted Status
                state.status = res.status || intendedStatus;

                if (notify) {
                    const showToast = (msg) => {
                        const t = document.createElement('div');
                        t.className = 'fixed bottom-10 left-1/2 transform -translate-x-1/2 bg-zinc-800 text-white px-6 py-3 rounded-full shadow-2xl z-[10000] border border-zinc-700 animate-fade-in';
                        t.innerHTML = `<span class="material-symbols-outlined text-green-400">check_circle</span> <span>${msg}</span>`;
                        document.body.appendChild(t);
                        setTimeout(() => t.remove(), 2500);
                    };

                    if (res.status === 'published') showToast(window.locale.t('editor.publish_complete'));
                    else if (res.isApprovalRequest) {
                        // [FIX] Use Premium Approval Modal
                        window.Modal.showApprovalRequestSuccess();
                    }
                    else if (state.status === 'pending') showToast(window.locale.t('common.pending') || "Pending");
                    else showToast(window.locale.t('editor.save_complete'));

                    // [NEW] Automation Trigger on Publish
                    if (targetStatus === 'published' && !res.isApprovalRequest) {
                        // 1. Image OCR (Efficient)
                        this._triggerImageAnalysis(this.seriesId, this.currentEpisodeId, state.blocks);

                        // 2. Text Analysis (Costly - Only on Publish)
                        DaVinciService.triggerTextAnalysis(this.seriesId, this.currentEpisodeId, state.blocks);
                    }
                }

                if (silent) {
                    const indicator = document.getElementById('auto-save-indicator');
                    if (indicator) {
                        indicator.style.opacity = '1';
                        setTimeout(() => indicator.style.opacity = '0', 2000);
                    }
                    console.log("[DaVinci] Auto Saved");
                }

                // [FIX] Auto Publish Series if needed (Handled by Service now, but kept for cache update?)
                // Service handles it.
            } else {
                throw new Error(res.error);
            }

            // [FIX] Auto Publish Series if needed
            // [FIX] Auto Publish Series is now handled by DaVinciService.saveEpisode (Gate Logic)
            // No need to call saveSeries separately here.

        } catch (e) {
            console.error("Save Error:", e);
            if (notify) window.Modal.alert("저장 실패: " + e.message);
        }
    }

    async _triggerImageAnalysis(seriesId, episodeId, blocks) {
        try {
            // Check if there are images
            const hasImages = (blocks || []).some(b => b.type === 'image' && b.url);
            if (!hasImages) return;

            console.log("[DaVinci] Auto-Triggering Image Analysis (OCR)...");
            window.Modal.toast("AI 이미지 분석이 시작되었습니다...", "AI 처리");

            const { getFunctions, httpsCallable } = await import("firebase/functions");
            const functions = getFunctions(app);
            const triggerOCR = httpsCallable(functions, 'manuallyTriggerOCR');

            // Fire & Forget (Don't await result to avoid blocking UI)
            triggerOCR({
                mode: 'davinci',
                seriesId: seriesId,
                episodeId: episodeId,
                uid: window.user.uid
            }).then(res => {
                const data = res.data;
                console.log("[DaVinci] Auto-OCR Result:", data);
                if (data.successCount > 0) {
                    window.Modal.toast(`이미지 분석 완료: ${data.successCount}장`, "AI 완료");
                }
            }).catch(err => {
                console.warn("[DaVinci] Auto-OCR Background Warning:", err);
            });

        } catch (e) {
            console.error("[DaVinci] Failed to trigger auto-analysis:", e);
        }
    }
}
